import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plug, RefreshCw, ExternalLink, ChevronDown } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

const Row = ({ k, label, hint, placeholder, form, set, type = "text" }) => (
  <div>
    <Label>{label}</Label>
    <Input data-testid={`settings-${k.replace(/_/g, "-")}-input`} type={type} className="mt-2" value={form[k] || ""} onChange={(e) => set(k)(e.target.value)} placeholder={placeholder} />
    {hint && <p className="text-xs text-[#8C8275] mt-2">{hint}</p>}
  </div>
);

export const ShopifySection = ({ form, set, save }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [guide, setGuide] = useState(false);
  const [params, setParams] = useSearchParams();
  const load = () => api.get("/admin/shopify/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (params.get("shopify") === "connesso") { toast.success("Shopify autorizzato con successo"); setParams({}); load(); }
  }, [params, setParams]);

  const redirectUri = `${process.env.REACT_APP_BACKEND_URL}/api/shopify/callback`;

  const connect = async () => {
    setBusy(true);
    try {
      if (!(await save())) return;
      const { data } = await api.post("/admin/shopify/connect");
      setStatus(data);
      toast.success("Shopify connesso");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const authorize = async () => {
    setBusy(true);
    try {
      if (!(await save())) return;
      const { data } = await api.get("/admin/shopify/install-url", { params: { redirect_uri: redirectUri } });
      window.location.href = data.url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/shopify/sync-products");
      toast.success(`Sincronizzati ${data.total} prodotti: ${data.created} nuovi, ${data.updated} aggiornati`);
      load();
    } catch (e) { toast.error(errMsg(e)); load(); } finally { setBusy(false); }
  };

  const sync_ = status?.last_sync;

  return (
    <div className="bg-white border border-[#E2DACF] p-6 mt-6 space-y-6" data-testid="settings-shopify-catalog-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-serif text-xl">Catalogo Shopify</p>
          <p className="text-sm text-[#8C8275] mt-2">Importa i prodotti veri del negozio (nome, prezzo, immagine, disponibilità, Variant ID) e i tag per il motore di consiglio. Con il negozio pubblico basta "Sincronizza prodotti": legge il catalogo da <code className="text-xs">{form.shop_domain || "dominio"}/products.json</code>. La connessione all'Admin API (sotto) è facoltativa e serve solo se il negozio torna protetto da password.</p>
        </div>
        <span data-testid="shopify-connection-badge" className={`text-[11px] px-2 py-1 border whitespace-nowrap ${status?.connected || status?.synced_products ? "bg-[#F1F8F2] text-[#2F6B3A] border-[#CFE3D3]" : "bg-[#F5F3EF] text-[#8C8275] border-[#E6DFD5]"}`}>
          {status?.connected ? `Admin API · ${status.connection.mode === "oauth" ? "autorizzato" : "credenziali"}` : status?.synced_products ? `${status.synced_products} prodotti · catalogo pubblico` : "Non sincronizzato"}
        </span>
      </div>

      <div className="flex flex-wrap gap-3">
        <button data-testid="shopify-sync-btn" className="fs-btn" onClick={sync} disabled={busy}><RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Sincronizza prodotti</button>
      </div>

      <details className="text-sm border-t border-[#F2ECE4] pt-4" data-testid="shopify-admin-api-details">
        <summary className="cursor-pointer text-[#595652]">Connessione Admin API (facoltativa, solo se il negozio è protetto da password)</summary>
        <ol className="text-sm text-[#595652] space-y-2 list-decimal pl-5 leading-relaxed mt-3">
          <li>Apri il <a href="https://dev.shopify.com/dashboard/" target="_blank" rel="noreferrer" className="underline underline-offset-4">Dev Dashboard Shopify</a> → <strong>Apps → Create app → Start from Dev Dashboard</strong>, nome "FineSkin Advisor".</li>
          <li>Nella scheda <strong>Versions</strong>: App URL <code className="text-xs bg-[#FAF8F5] px-1">https://shopify.dev/apps/default-app-home</code>, scope <strong>read_products</strong>, URL di reindirizzamento consentito:
            <code data-testid="settings-shopify-redirect-uri" className="block text-xs bg-[#FAF8F5] border border-[#E6DFD5] px-3 py-2 mt-1 break-all">{redirectUri}</code>
            poi <strong>Release</strong>.</li>
          <li>Clicca <strong>Install app</strong> e scegli il negozio FineSkin.</li>
          <li>In <strong>Settings</strong> copia <strong>Client ID</strong> e <strong>Client secret</strong> e incollali qui.</li>
        </ol>
        <div className="grid sm:grid-cols-2 gap-5 mt-4">
          <Row k="shopify_shop" label="Dominio myshopify" placeholder="nome-negozio.myshopify.com" form={form} set={set}
            hint="Lo trovi nell'URL del pannello Shopify (admin.shopify.com/store/NOME) o in Impostazioni → Dominii." />
          <Row k="shopify_client_id" label="Client ID" placeholder="es. 1a2b3c…" form={form} set={set} />
          <Row k="shopify_client_secret" label="Client secret" placeholder="shpss_…" form={form} set={set} hint="Mostrato mascherato dopo il salvataggio." />
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <button data-testid="shopify-connect-btn" className="fs-btn-ghost" onClick={connect} disabled={busy}><Plug size={14} /> Connetti</button>
          <button data-testid="shopify-authorize-btn" className="fs-btn-ghost" onClick={authorize} disabled={busy}><ExternalLink size={14} /> Autorizza sul negozio</button>
        </div>
        <p className="text-xs text-[#8C8275] mt-3">"Connetti" funziona se negozio e app sono nella stessa organizzazione Shopify. In caso contrario usa "Autorizza sul negozio": si apre Shopify e ti chiede di approvare l'app.</p>
      </details>

      {sync_ && (
        <div data-testid="shopify-sync-status" className={`text-xs p-3 border ${sync_.ok ? "border-[#CFE3D3] bg-[#F1F8F2] text-[#2F6B3A]" : "border-[#EBD3C9] bg-[#FBF3EF] text-[#A0482F]"}`}>
          <p className="font-medium">Ultima sincronizzazione · {new Date(sync_.ts).toLocaleString("it-IT")}{sync_.mode === "catalogo_pubblico" ? " · catalogo pubblico" : sync_.mode ? " · Admin API" : ""} · {sync_.ok ? `${sync_.total} prodotti (${sync_.created} nuovi, ${sync_.updated} aggiornati, ${sync_.skipped} esclusi, ${sync_.deactivated} disattivati)` : "fallita"}</p>
          {sync_.error && <p className="mt-1">{sync_.error}</p>}
          {sync_.last_error && new Date(sync_.last_error.ts) > new Date(sync_.ts || 0) && <p className="mt-1 text-[#A0482F]">Ultimo tentativo fallito ({new Date(sync_.last_error.ts).toLocaleString("it-IT")}): {sync_.last_error.error}</p>}
          {sync_.unmapped?.length > 0 && <p className="mt-1 text-[#8C6F3E]">Senza step routine (assegnalo in Prodotti o aggiungi il tag step:… su Shopify): {sync_.unmapped.join(", ")}</p>}
        </div>
      )}

      <button type="button" data-testid="shopify-tag-guide-toggle" onClick={() => setGuide((g) => !g)} className="flex items-center gap-2 text-sm text-[#1A1A18]">
        <ChevronDown size={14} className={`transition-transform ${guide ? "rotate-180" : ""}`} /> Guida ai tag da usare su Shopify
      </button>
      {guide && status && (
        <div className="text-xs text-[#595652] space-y-4 border-t border-[#F2ECE4] pt-4" data-testid="shopify-tag-guide">
          <p>Aggiungi questi tag ai prodotti su Shopify (campo "Tag" della scheda prodotto). L'app li legge ad ogni sincronizzazione; tutti gli altri tag vengono ignorati.</p>
          <div>
            <p className="font-medium text-[#1A1A18] mb-1">Step della routine (uno per prodotto)</p>
            <p className="flex flex-wrap gap-1.5">{status.steps.map((s) => <code key={s.key} className="bg-[#FAF8F5] border border-[#E6DFD5] px-1.5 py-0.5">step:{s.key}</code>)}</p>
            <p className="mt-1 text-[#8C8275]">In alternativa imposta il "Tipo di prodotto" Shopify uguale al nome dello step (es. "Detergente").</p>
          </div>
          <div>
            <p className="font-medium text-[#1A1A18] mb-1">Compatibilità (quanti ne vuoi)</p>
            <p className="flex flex-wrap gap-1.5">{status.tags.map((t) => <code key={t.key} title={t.label} className="bg-[#FAF8F5] border border-[#E6DFD5] px-1.5 py-0.5">{t.key}</code>)}</p>
          </div>
          <div>
            <p className="font-medium text-[#1A1A18] mb-1">Opzionali</p>
            <ul className="space-y-1">
              <li><code className="bg-[#FAF8F5] px-1">exclude:sens:alta</code> → non proporre a chi ha quel tag (es. pelle molto sensibile)</li>
              <li><code className="bg-[#FAF8F5] px-1">moment:day</code> / <code className="bg-[#FAF8F5] px-1">moment:night</code> → solo giorno o solo sera (default entrambi)</li>
              <li><code className="bg-[#FAF8F5] px-1">priority:80</code> → priorità commerciale 0–100 (default 50)</li>
              <li><code className="bg-[#FAF8F5] px-1">advisor:skip</code> → non importare questo prodotto</li>
            </ul>
          </div>
          <p className="text-[#8C8275]">I tuoi hashtag esistenti (es. #serum, #niacinamide, #centella) vengono tradotti automaticamente secondo la sezione <a href="/admin/regole-tag" className="underline underline-offset-4 text-[#1A1A18]">Regole tag</a>{status.rules ? ` (${status.rules} regole attive)` : ""}. Testi "Perché per te" e "Come si usa" si scrivono nella sezione Prodotti e non vengono sovrascritti dalla sincronizzazione.</p>
        </div>
      )}
    </div>
  );
};
